import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';

interface ChatMessage {
  id: string;
  message: string;
  msg_from: 'user' | 'gpt';
  timestamp?: string;
}

export interface PlaybookChatConfig {
  playbookId?: string;
  playbookName?: string;
  userId?: string;
  useDynamic?: boolean;
}

interface ModernChatStore {
  chatMessages: ChatMessage[];
  currWebSocket: WebSocket | null;
  pendingResponse: boolean;
  currentConfig: PlaybookChatConfig | null;
  connectionId: string | null;
  
  // Loading states
  isConnecting: boolean;
  isPlaybookReady: boolean;
  connectionError: string | null;
  
  clearChatMessages: () => void;
  resetChatState: () => void; // New comprehensive reset function
  addToChatMessage: (msg: string, msg_from: 'user' | 'gpt') => void;
  initChatWebSocket: (config?: PlaybookChatConfig) => void;
  sendChatMessageAsync: (msg: string) => Promise<void>;
  disconnect: () => void;
}

const getWebSocketUrl = (config?: PlaybookChatConfig): string => {
  // Use environment variable like other services, fallback to production
  const baseUrl = process.env.REACT_APP_AUTOGEN_BASE_URL || 'https://genie-autogen.azurewebsites.net';
  
  // Convert HTTP(S) to WebSocket protocol properly
  let wsBaseUrl;
  if (baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1')) {
    // Local development always uses ws://
    wsBaseUrl = baseUrl.replace(/^https?:\/\//, 'ws://');
  } else {
    // Production: use ws:// for http://, wss:// for https://
    if (baseUrl.startsWith('https://')) {
      wsBaseUrl = baseUrl.replace(/^https:\/\//, 'wss://');
    } else {
      wsBaseUrl = baseUrl.replace(/^http:\/\//, 'ws://');
    }
  }
  
  const ApiPath = `${wsBaseUrl}/ws/`;
  const chat_id = uuidv4();
  
  // Base WebSocket URL - just the session ID as path
  let wsUrl = ApiPath + chat_id;
  
  // Create query parameters
  const params = new URLSearchParams();
  
  // Add authentication token - required for websocket authentication
  const authToken = localStorage.getItem('accessToken');
  if (authToken) {
    params.append('token', authToken);
  }
  
  // Add query parameters for dynamic playbook support
  if (config && config.playbookId) {
    // Backend expects snake_case parameters
    params.append('playbook_id', config.playbookId);
    params.append('use_dynamic', (config.useDynamic ?? true).toString());
    
    if (config.userId) {
      params.append('user_id', config.userId);
    }
  }
  
  // Add parameters to URL
  if (params.toString()) {
    wsUrl += '?' + params.toString();
  }
  
  return wsUrl;
};

export const useModernChatStore = create<ModernChatStore>((set, get) => ({
  chatMessages: [],
  currWebSocket: null,
  pendingResponse: false,
  currentConfig: null,
  connectionId: null,
  
  // Loading states
  isConnecting: false,
  isPlaybookReady: false,
  connectionError: null,

  clearChatMessages: () => set({ chatMessages: [] }),
  
  resetChatState: () => {
    // Disconnect any existing WebSocket
    const currentWs = get().currWebSocket;
    if (currentWs) {
      currentWs.close();
    }
    
    // Reset all state to initial values
    set({
      chatMessages: [],
      currWebSocket: null,
      pendingResponse: false,
      currentConfig: null,
      connectionId: null,
      isConnecting: false,
      isPlaybookReady: false,
      connectionError: null
    });
  },
  
  addToChatMessage: (msg: string, msg_from: 'user' | 'gpt') => {
    let cleanedMessage = msg;
    
    // Clean up agent messages by removing BRKT or TERMINATE suffixes
    if (msg_from === 'gpt') {
             // Remove various forms of BRKT/TERMINATE from the end of messages
       cleanedMessage = msg
         .replace(/\s*\[?BRKT\.?\]?\s*$/i, '')         // Remove [BRKT], BRKT, BRKT., or variations at end
         .replace(/\s*\[?TERMINATE\]?\s*$/i, '')       // Remove [TERMINATE], TERMINATE, or variations at end
         .trim();                                      // Clean up any remaining whitespace
      
      // If the message becomes empty after cleaning, don't show it
      if (!cleanedMessage) {
        return;
      }
    }

    set((state) => ({
      chatMessages: [
        ...state.chatMessages,
        {
          id: `${msg_from}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          message: cleanedMessage,
          msg_from: msg_from,
          timestamp: new Date().toISOString(),
        }
      ]
    }));
  },

  initChatWebSocket: (config?: PlaybookChatConfig) => {
    // Generate unique connection ID for this session
    const newConnectionId = uuidv4();
    const currentConnectionId = get().connectionId;
    
    // Set loading states
    set({ 
      isConnecting: true, 
      isPlaybookReady: false, 
      connectionError: null,
      connectionId: newConnectionId 
    });
    
    // Only disconnect if this is a reconnection from the same component instance
    const currentWs = get().currWebSocket;
    if (currentWs && currentWs.readyState === WebSocket.OPEN && currentConnectionId) {
      console.log('🔄 Reconnecting same instance - closing existing connection');
      currentWs.close();
    }
    
    const chat_id = uuidv4();
    const wsUrl = getWebSocketUrl(config);
    
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = (event) => {
      // Only update state if this connection is still the current one
      const currentConnId = get().connectionId;
      if (currentConnId === newConnectionId || !currentConnId) {
        set({ 
          currWebSocket: ws, 
          currentConfig: config,
          connectionId: newConnectionId,
          isConnecting: false,
          isPlaybookReady: true, // Mark as ready when WebSocket connects
          connectionError: null
        });
      } else {
        ws.close();
      }
    };
    
    ws.onmessage = (event) => {
      get().addToChatMessage(event.data, "gpt");
      set({ pendingResponse: false });
    };
    
    ws.onclose = (event) => {
      const currentConnId = get().connectionId;
      
      // Only clear state if this was the active connection
      if (currentConnId === newConnectionId) {
        let errorMessage = null;
        
        // Handle specific error codes
        if (event.code === 4001) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (event.code === 1006) {
          errorMessage = 'Connection lost unexpectedly';
        } else if (event.code !== 1000) {
          // 1000 is normal closure
          errorMessage = `Connection closed: ${event.reason || 'Unknown error'}`;
        }
        
        set({ 
          currWebSocket: null, 
          pendingResponse: false, 
          currentConfig: null, 
          connectionId: null,
          isConnecting: false,
          isPlaybookReady: false,
          connectionError: errorMessage
        });
      }
    };
    
    ws.onerror = (error) => {
      set({ 
        pendingResponse: false,
        isConnecting: false,
        isPlaybookReady: false,
        connectionError: 'Failed to connect to chat service'
      });
    };
  },

  disconnect: () => {
    const currentWs = get().currWebSocket;
    const currentConnId = get().connectionId;
    if (currentWs) {
      currentWs.close();
      set({ currWebSocket: null, pendingResponse: false, currentConfig: null, connectionId: null });
    }
  },

  sendChatMessageAsync: async (msg: string) => {
    try {
      const { currWebSocket, isPlaybookReady, isConnecting } = get();
      
      // Check if we're ready to send messages
      if (!isPlaybookReady || isConnecting) {
        return;
      }
      
      if (currWebSocket && currWebSocket.readyState === WebSocket.OPEN) {
        get().addToChatMessage(msg, "user");
        currWebSocket.send(msg);
        set({ pendingResponse: true });
      } else {
        set({ 
          connectionError: 'Connection lost. Please refresh the page.',
          isPlaybookReady: false 
        });
      }
    } catch (err) {
      set({ 
        pendingResponse: false,
        connectionError: 'Failed to send message. Please try again.'
      });
    }
  },
})); 